import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageContext } from '../context/MessageContext';
import { AuthContext } from '../context/AuthContext';
import Icons from '../components/Icons';
import Notifications from '../components/Notifications';
import Chat from '../components/Chat';

const CarOffers = () => {
    const navigate = useNavigate();
    const { user, serverNotAvailable, loading: authLoading } = useContext(AuthContext);
    const { setMess } = useContext(MessageContext);
    const [offers, setOffers] = useState([]);
    const [activeTab, setActiveTab] = useState('new');
    const [isScrolled, setIsScrolled] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handleScroll = () => { setIsScrolled(window.scrollY > 10); };  // после 10px прячем текст
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Перенаправление на /CreateCarOffer для редактирования предложения
    const handleEditClick = (request) => {
        navigate("/createCarOffer", {
            state: {
                model_name_en: request.model_name_en,
                description: request.description,
                id_car_request: request.id_car_request,
                id_car_offer: request.id_car_offer,
                max_price: request.price,
                id_currency: request.id_currency,
                mileage: request.mileage,
                valid_until: request.valid_until,
                year: request.year,
                status: request.status,
                published: request.published,
                customs_cities: request.id_customs_cities?.split(',').map(Number),
                border_cities: request.id_border_cities?.split(',').map(Number)
            }
        });
    };

    useEffect(() => {
        const fetchOffers = async () => {
            if (!user?.id) return;
            setLoading(true);
            try {
                const response = await axios.get(`/api/car-offer/manager/${user.id}`);
                setOffers(response.data);
            } catch (err) {
                setMess({ color: 'red', title: 'Ошибка загрузки предложений:', desc: err.response?.data?.NoValidToken ?? err.message });
            } finally {
                setLoading(false);
            }
        };
        if (!authLoading && user) fetchOffers();
    }, [user, authLoading, setMess]);

    const handlePublishToggle = async (offerId, published) => {
        try {
            await axios.put(`/api/car-offer/${offerId}/publish`, { published });
            setOffers((prev) =>
                prev.map((offer) =>
                    offer.id_car_offer === offerId ? { ...offer, published } : offer
                )
            );
            setMess({ color: 'green', title: 'Успешно:', desc: `Предложение ${published ? 'опубликовано' : 'снято с публикации'}` });
        } catch (err) {
            setMess({ color: 'red', title: 'Ошибка изменения публикации:', desc: err.message });
        }
    };

    const offerCounts = offers.reduce((acc, offer) => {
        acc[offer.status] = (acc[offer.status] || 0) + 1;
        return acc;
    }, {});

    // Фильтрация предложений по активной вкладке
    const filteredOffers = offers.filter((offer) => {
        if (activeTab === 'new') return offer.status === 1;         //'Новое';
        if (activeTab === 'viewed') return offer.status === 2;      //'Просмотрено';
        if (activeTab === 'inProgress') return offer.status === 3;      //'В работе';
        if (activeTab === 'rejected') return offer.status === 4;    // 'Отклонено';
        if (activeTab === 'archived') return offer.status === 5;    // 'В архиве';
        return true;
    });

    const offersForChat = offers.filter((offer) => {
        return (new Date(offer.valid_until).getTime() - Date.now()) > 0 && offer.published == 1 && offer.status < 4 || offer.status == 3;  // 'Новые, Просмотрено' или 'В работе' и Опубликованные и Актуальные
    });

  // Сообщение "Сервер не доступен"
  if (serverNotAvailable) return (
    <section className="py-10">
      <div className="flex justify-center px-4">
        <div className="flex w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center w-12 bg-yellow-500">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="px-4 py-3">
            <div className="flex flex-col">
              <span className="textyellow-500 dark:text-yellow-400">Ошибка загрузки данных</span>
              <p className="text-sm text-gray-600 dark:text-gray-200">
                Сервер не доступен
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

    if (authLoading || loading) return (<div className="flex justify-center p-4"><Icons name="Spinner" /> </div>);

    if (!user && !loading)
        return (
            <section className="py-10">
                <div className="flex justify-center px-4">
                    <div className="flex w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center w-12 bg-green-500">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="px-4 py-3">
                            <div className="flex flex-col">
                                <span className="text-green-500 dark:text-green-400">Требуется авторизация</span>
                                <p className="text-sm text-gray-600 dark:text-gray-200">
                                    Пожалуйста, войдите в систему, чтобы продолжить.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );

    if (!offers.length && !loading && !authLoading )
        return (
            <section className="py-20">
                <div className="grid mx-auto px-4 place-items-center">
                    <div className="flex w-full max-w-sm overflow-hidden bg-white rounded-lg shadow-md dark:bg-gray-800">
                        <div className="flex items-center justify-center w-12 bg-yellow-400">
                            <svg
                                className="w-6 h-6 text-white fill-current"
                                viewBox="0 0 40 40"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M20 3.33331C10.8 3.33331 3.33337 10.8 3.33337 20C3.33337 29.2 10.8 36.6666 20 36.6666C29.2 36.6666 36.6667 29.2 36.6667 20C36.6667 10.8 29.2 3.33331 20 3.33331ZM21.6667 28.3333H18.3334V25H21.6667V28.3333ZM21.6667 21.6666H18.3334V11.6666H21.6667V21.6666Z" />
                            </svg>
                        </div>
                        <div className="px-4 py-2 -mx-3">
                            <div className="mx-3">
                                <span className="text-yellow-400 dark:text-yellow-300">Уведомление</span>
                                <p className="text-sm text-gray-600 dark:text-gray-200">Предложения не найдены</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section >
        );

    return (
        <section className="px-4 py-4 md:py-8">
            <div className="container  max-w-7xl mx-auto lg:px-4">
                <div className="sticky top-0 z-50 bg-gray-50 dark:bg-gray-900 gap-2 flex mb-2 pb-2 justify-center ">
                    {['new', 'viewed', 'inProgress', 'rejected', 'archived'].map((tab) => (
                        <button
                            key={tab}
                            className={`text-xs sm:text-base p-2 w-20 sm:w-40 items-center justify-center flex-col md:flex-row relative rounded-md transition-all duration-300 group  ${activeTab === tab
                                ? 'text-white buttonGreen'
                                : 'buttonGray'
                                }
                                ${tab === 'archived' ? 'hidden md:flex' : 'flex'}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab === 'new' && (
                                <>
                                    <Icons name="NewIcon" classNameIcons="inline-block group-hover:scale-125" />

                                    <span className={`ml-1 transition-opacity duration-300 ${isScrolled ? "opacity-0 h-0 overflow-hidden sm:opacity-100 sm:h-auto" : "opacity-100"}`}>
                                        Новые
                                    </span>
                                    {offerCounts[1] > 0 && (
                                        <span className="notification-badge-left">{offerCounts[1]}</span>
                                    )}
                                </>
                            )}
                            {tab === 'viewed' && (
                                <>
                                    <Icons name="ViewedIcon" classNameIcons="group-hover:scale-125" />

                                    <span className={`ml-1 transition-opacity duration-300 ${isScrolled ? "opacity-0 h-0 overflow-hidden" : "opacity-100"}`}>
                                        Просмотрено
                                    </span>
                                    {offerCounts[2] > 0 && (
                                        <span className="notification-badge-left">{offerCounts[2]}</span>
                                    )}
                                </>
                            )}
                            {tab === 'inProgress' && (
                                <>
                                    <Icons name="inProgressIcon" classNameIcons="group-hover:scale-125" />

                                    <span className={`ml-1 transition-opacity duration-300 ${isScrolled ? "opacity-0 h-0 overflow-hidden" : "opacity-100"}`}>
                                        В работе
                                    </span>
                                    {offerCounts[3] > 0 && (
                                        <span className="notification-badge-left">{offerCounts[3]}</span>
                                    )}
                                    {/* <span className="hidden lg:inline-block ml-1">&#128295;</span> иконка шестеренки */}
                                    <Notifications />
                                </>
                            )}
                            {tab === 'rejected' && (
                                <>
                                    <Icons name="RejectedIcon" classNameIcons="group-hover:scale-125" />

                                    <span className={`ml-1 transition-opacity duration-300 ${isScrolled ? "opacity-0 h-0 overflow-hidden" : "opacity-100"}`} >
                                        Отклонено
                                    </span>
                                    {offerCounts[4] > 0 && (
                                        <span className="notification-badge-left">{offerCounts[4]}</span>
                                    )}
                                </>
                            )}
                            {tab === 'archived' && (
                                <>
                                    <Icons name="ArchivedIcon" classNameIcons="group-hover:scale-125" />

                                    <span className={`ml-1 transition-opacity duration-300 ${isScrolled ? "opacity-0 h-0 overflow-hidden" : "opacity-100"}`} >
                                        Архив
                                    </span>
                                    {offerCounts[5] > 0 && (
                                        <span className="notification-badge-left">{offerCounts[5]}</span>
                                    )}
                                </>
                            )}
                        </button>
                    ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredOffers.map((offer) => (
                        <div
                            key={offer.id_car_offer}
                            className="bg-white dark:bg-gray-800 border dark:border-0 border-gray-200  rounded-lg shadow-md p-4 flex flex-col justify-between transition-all duration-200 hover:shadow-lg"
                        >
                            <div className='text-sm text-gray-600 dark:text-gray-400'>
                                <h3 className="text-lg text-gray-800 dark:text-white mb-2 line-clamp-2">
                                    {offer.brand_name_en} {offer.model_name_en} {offer.trim_name}
                                </h3>
                                <p className="mb-1 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Цена: {offer.price} {offer.symbol_curr}
                                </p>
                                <p className="mb-1 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Год: {offer.year ? new Date(offer.year).getFullYear() : 'N/A'}
                                </p>
                                <p className="mb-1 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                    Пробег: {offer.mileage ? `${offer.mileage} км` : 'N/A'}
                                </p>
                                <p className="mb-1 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                    </svg>
                                    Описание: {offer.description ? offer.description.substring(0, 50) + '...' : 'Нет описания'}
                                </p>
                                <p className="mb-1 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Действительно до: {offer.valid_until ? new Date(offer.valid_until).toLocaleDateString() : 'N/A'}
                                </p>

                                <p className="mb-1 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 " fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <rect x="4" y="4" width="16" height="19" rx="2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                                        <path d="M8 8h8M8 12h5" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                                        <circle cx="15" cy="17.5" r="3.5" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1"></circle>
                                        <path d="M13.5 17.5l1 1 2-2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" stroke="#009900" />
                                    </svg>
                                    Города таможни: {offer.customs_cities ? offer.customs_cities : 'N/A'}
                                </p>
                                <p className="mb-1 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 0v3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                                        <path d="M17 8H5l2.5-2.5-2.5-2.5h12l2.5 2.5z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                                        <path d="M12 8v3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                                        <path d="M7 16h12l-2.5-2.5 2.5-2.5h-12l-2.5 2.5z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path><path d="M12 16v8" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                                    </svg>
                                    Города границы: {offer.border_cities ? offer.border_cities : 'N/A'}
                                </p>


                                <p className="mb-1 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Клиент: {offer.name || 'N/A'} {offer.surname ? ` ${offer.surname}` : ''}
                                </p>
                                {offer.status === 1 && (  // Только для "Новых" предложений}
                                    <>
                                        {offer.published === 0 && (
                                            <div className="flex items-center justify-between">
                                                <button
                                                    className="px-3 py-1 rounded-md text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-800 transition-colors duration-200"
                                                    onClick={() => handleEditClick(offer)}
                                                >
                                                    Изменить
                                                </button>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between">
                                            <p className="flex items-center gap-2">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={offer.published ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
                                                </svg>
                                                Статус: {offer.published ? 'Опубликовано' : 'Не опубликовано'}
                                            </p>
                                            <button
                                                className={`px-3 py-1 rounded-md text-sm ${offer.published ? 'buttonGreen text-gray-100' : 'buttonGray'} transition-colors duration-200`}
                                                onClick={() => handlePublishToggle(offer.id_car_offer, offer.published ? 0 : 1)}
                                            >
                                                {offer.published ? 'Снять с публикации' : 'Опубликовать'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <Chat offers={offersForChat} />
            </div>
        </section>
    );
};

export default CarOffers;